import type { EngagementResult } from '@exigo/shared';
import { Card } from '../../components/ui/Card';
import styles from './NotRelevantList.module.css';

/**
 * Engagements that do not apply to the deal as scoped.
 *
 * These are not blocked — no gate is refusing them. There is simply no
 * consumption against the services they are written for, so they cannot be
 * sized at all. That makes them reference material rather than something to act
 * on, which is why this is a flat read-only list rather than the expandable
 * rows used for engagements that are genuinely in play: bands, payouts and
 * proof-of-execution detail mean nothing for an engagement with no figure
 * behind it, and twenty expandable cards buried the sections that matter.
 */
export function NotRelevantList({ rows }: { rows: EngagementResult[] }): React.JSX.Element {
  return (
    <>
      <p className={styles.title}>Not relevant to the workloads entered</p>
      <p className={styles.note}>
        No consumption was entered against the services these engagements are written for. Add a
        workload row and they will reappear.
      </p>
      <Card className={styles.card}>
        <ul className={styles.list}>
          {rows.map((row) => (
            <li key={row.key} className={styles.item}>
              <b className={styles.name}>{row.name}</b>
              {relevanceReason(row) ? (
                <span className={styles.reason}> — {relevanceReason(row)}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

/**
 * Why the engagement does not apply, taken from the workload-relevance gate.
 *
 * Read from the gate rather than restated here: the engine already words this
 * per engagement ("No consumption entered against Fabric or Databricks, so…"),
 * and a second phrasing in the UI would be one more thing to drift.
 */
function relevanceReason(row: EngagementResult): string | null {
  return row.gates.find((g) => g.label === 'Workload relevance')?.detail ?? null;
}
